import { computed, inject, Type } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { delay, pipe, switchMap, tap } from 'rxjs';

import { SUBMISSION_DOCUMENT_TYPES_INITIAL_STATE } from '../../constants';
import {
  ConfigSubmissionsData,
  SDocumentPayload,
  SDocumentType,
  SDocumentTypePayload,
  SubmissionDocumentTypesListSignalState,
} from '../../types';

import { WcgTableValues } from '@shared/interfaces/wcg-table-utils.interface';
import { SubmissionService } from '../../services';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import { withDocumentTypesEntities } from '../features';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { OpenDialogState, withDialogFeatures } from '@data-access/shared';

export const SUBMISSION_DOCUMENT_TYPES_SIGNAL_STORE = signalStore(
  withState(() => inject(SUBMISSION_DOCUMENT_TYPES_INITIAL_STATE)),
  withProps(({ ...store }) => ({
    _submissionService: inject(SubmissionService),
    _wcgTableUtils: inject(WcgTableUtilsService),
    store,
  })),
  withFeature(() => withDialogFeatures()),
  withFeature(() => withDocumentTypesEntities()),
  withComputed(
    ({
      attributes,
      canCloseDialog,
      documentTypes,
      submission,
      isLoading,
      _sdocument_typesEntities,
      error,
      entity_type,
    }) => ({
      $attributeList: computed(() => {
        const _attributes = attributes();
        if (!_attributes.length) {
          return [] as WcgMenuOption[];
        }
        return _attributes.map((attribute) => ({
          id: attribute.id,
          label: attribute.name,
        }));
      }),
      $canCloseDialog: computed(() => canCloseDialog()),
      $data: computed(() => _sdocument_typesEntities()),
      $documentList: computed(() => {
        const _documentList = documentTypes();
        if (!_documentList.length) {
          return [] as WcgMenuOption[];
        }
        return _documentList.map((document) => ({
          id: document.id,
          label: document.name,
        }));
      }),
      $entityType: computed(() => entity_type()),
      $error: computed(() => error()!),
      $isLoading: computed(() => isLoading()),
      $submission: computed(() => submission()),
      $totalCount: computed(() => submission().document_types.length),
    }),
  ),
  withMethods(({ _submissionService, _wcgTableUtils, ...store }) => {
    const _updateDocumentTypeEntities = (
      action: string,
      documentType: SDocumentType,
    ) => {
      switch (action.toLowerCase()) {
        case 'create':
          store.addDocumentType(documentType);
          break;
        case 'update':
          store.updateDocumentType(documentType);
          break;
      }
    };

    const _loadSubmissionDocumentTypes = rxMethod<{
      entity_type: string;
      workspaceTypeId: string;
    }>(
      pipe(
        tap(({ entity_type, workspaceTypeId }) =>
          patchState(store, {
            entity_type: entity_type,
            isLoading: true,
            workspaceTypeId: workspaceTypeId,
          }),
        ),
        switchMap(({ entity_type, workspaceTypeId }) =>
          _submissionService
            .getSubmissionDocumentTypeList({
              entity_type,
              id: workspaceTypeId,
            } as ConfigSubmissionsData)
            .pipe(
              delay(400),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (submissionDocument) => {
                  patchState(store, {
                    entity_type: entity_type,
                    submission: submissionDocument,
                  });
                  store.setDocumentTypes(submissionDocument.document_types);
                },
              }),
            ),
        ),
      ),
    );
    const _updateSubmissionDocumentType = rxMethod<SDocumentTypePayload>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap((documentTypePayload) => {
          const docPayload: SDocumentPayload = {
            entityType: store.entity_type(),
            payload: [{ ...documentTypePayload }],
            submissionId: store.submission().id,
            wcgTableValues: {} as WcgTableValues,
          };

          return _submissionService
            .updateSubmissionDocumentTypes(docPayload)
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: () => {
                  patchState(store, { canCloseDialog: true });
                  const _document = store
                    .documentTypes()
                    .find(
                      (d) => d.id === documentTypePayload.document_type_id,
                    )!;
                  const _attribute = store
                    .attributes()
                    .find(
                      (a) => a.id === documentTypePayload.attribute_type_id,
                    )!;
                  _updateDocumentTypeEntities(documentTypePayload.action, {
                    attribute_type_id: _attribute.id,
                    attribute_type_name: _attribute.name,
                    id: _document.id,
                    name: _document.name,
                  });
                },
              }),
            );
        }),
      ),
    );

    const _loadAllDocumentTypes = rxMethod(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(() =>
          _submissionService.getSubmissionDocumentTypes().pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, { error: error, isLoading: false }),
              finalize: () => patchState(store, { isLoading: false }),
              next: (response) =>
                patchState(store, { documentTypes: [...response.data] }),
            }),
          ),
        ),
      ),
    );

    const _loadAllAttributes = rxMethod<{ params: WcgTableValues }>(
      pipe(
        tap(() => patchState(store, { isLoading: true })),
        switchMap(({ params }) =>
          _submissionService
            .getAllAttributes(_wcgTableUtils.generateTableQueryParams(params))
            .pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) =>
                  patchState(store, { attributes: [...response.data] }),
              }),
            ),
        ),
      ),
    );

    const _openDialog = (dialogState: OpenDialogState) => {
      patchState(store, { canCloseDialog: false, isLoading: false });
      store.openDialog(dialogState);
    };

    return {
      addSubmissionDocumentType: (documentTypePayload: SDocumentTypePayload) =>
        _updateSubmissionDocumentType({ ...documentTypePayload }),

      deleteSubmissionDocumentType: (
        documentTypePayload: SDocumentTypePayload,
      ) =>
        _updateSubmissionDocumentType({
          ...documentTypePayload,
          action: 'remove',
        }),
      loadAllAttributes: (params: WcgTableValues) =>
        _loadAllAttributes({ params }),
      loadAllDocumentTypes: () => _loadAllDocumentTypes({}),
      loadSubmissionDocumentTypes: (
        entity_type: string,
        workspaceTypeId: string,
      ) => _loadSubmissionDocumentTypes({ entity_type, workspaceTypeId }),
      openDocumentTypeDialog: (dialogState: OpenDialogState) =>
        _openDialog(dialogState),
    };
  }),
) satisfies Type<SubmissionDocumentTypesListSignalState>;

/**
 *  @NOTE
 *  The below code will give type for SUBMISSION_DOCUMENTTYPES_SIGNAL_STORE
 */
export type submissionDocumentTypesStore = InstanceType<
  typeof SUBMISSION_DOCUMENT_TYPES_SIGNAL_STORE
>;
